import { DraftOrderPayload } from './../models/payment.model';
import spacetime from 'spacetime';
import { snakeKeys } from 'js-convert-case';

import getFingerprint from 'bindings/fingerprint';
import { ProductFormInputs, PurchaseKind, Recipient } from 'models/purchaseForm.model';
import {
  GuestCheckoutFriendPayload,
  GuestCheckoutSelfPayload,
  UserCheckoutFriendPayload,
  UserCheckoutSelfPayload,
} from 'models/payment.model';
import { Sku } from 'models/sku.model';
import { User } from 'common/types';

export const encodeRecipients = (recipients: Recipient[]) => {
  return recipients.map(({ name, email, phone }) => ({
    name,
    email: email || undefined,
    phone: phone ? phone?.toString()?.trim().replaceAll(/\s/g, '') : undefined,
  }));
};

/**
 * Maps the product form inputs to the payload for the checkout request.
 * @param input - ProductFormInputs The input from the purchase form
 * @param cardUid - string The uid of the card to use
 * @param purchaseKind - PurchaseKind The kind of purchase (ex. guest or user)
 */
export const makeDraftOrderPayload = async (
  input: ProductFormInputs,
  cardUid: Sku['uid'],
  purchaseKind: PurchaseKind
): Promise<DraftOrderPayload> => {
  const deviceId = await getFingerprint();
  const basePayload = {
    status: 'draft',
    stripe_async_payment: true,
  };
  const { purchaseType } = input;

  const scheduledDeliveryAt = input.scheduledDeliveryAt
    ? spacetime(input.scheduledDeliveryAt).format('iso')
    : undefined;
  const recipientTimezone = input.scheduledDeliveryAt
    ? spacetime(input.scheduledDeliveryAt).timezone().name
    : undefined;

  if (purchaseType === 'self' && purchaseKind === 'user') {
    const userSelfPayload: UserCheckoutSelfPayload = {
      ...basePayload,
      skuUid: cardUid,
      promoCode: input.promo?.promoCode || '',
      themeUid: input?.giftCardDesignTab ? input?.customThemeUid || input?.themeUid || '' : '',
      value: input?.value,
      deviceId,
    };

    return snakeKeys(userSelfPayload) as UserCheckoutSelfPayload;
  } else if (purchaseType === 'self' && purchaseKind === 'guest') {
    const guestSelfPayload: GuestCheckoutSelfPayload = {
      ...basePayload,
      value: input?.value,
      promoCode: input?.promo?.promoCode || '',
      skuUid: cardUid,
      themeUid: input?.giftCardDesignTab ? input?.customThemeUid || input?.themeUid || '' : '',
      giftthemeUid: input?.giftTheme?.uid || '',
      guestFirstName: input?.guestFirstName || '',
      guestLastName: input?.guestLastName || '',
      guestEmail: input?.guestEmail || '',
      type: 'self',
    };

    return snakeKeys(guestSelfPayload) as GuestCheckoutSelfPayload;
  } else if (purchaseType === 'friend' && purchaseKind === 'user') {
    const userFriendPayload: UserCheckoutFriendPayload = {
      ...basePayload,
      value: input?.value,
      senderAlias: input?.senderAlias || undefined,
      promoCode: input?.promo?.promoCode || '',
      skuUid: cardUid,
      themeUid: input?.giftCardDesignTab ? input?.customThemeUid || input?.themeUid || '' : '',
      giftthemeUid: (input?.giftMediaTab && input?.giftTheme?.uid) || '',
      giftmediaUid: (input?.giftMediaTab && input?.giftMedia?.uid) || undefined,
      giphyId: input?.giftMediaTab && input?.giphy?.id,
      giphyUrl: input?.giftMediaTab && input?.giphy?.url,
      recipients: encodeRecipients(input?.recipients || []),
      scheduledDeliveryAt,
      recipientTimezone,
      message: (input?.personaliseMessageTab && input?.message) || '',
      deviceId,
    };

    return snakeKeys(userFriendPayload) as UserCheckoutFriendPayload;
  } else if (purchaseType === 'friend' && purchaseKind === 'guest') {
    const guestFriendPayload: GuestCheckoutFriendPayload = {
      ...basePayload,
      value: input?.value,
      senderAlias: input?.senderAlias || undefined,
      promoCode: input?.promo?.promoCode || '',
      skuUid: cardUid,
      themeUid: input?.giftCardDesignTab ? input?.customThemeUid || input?.themeUid || '' : '',
      giftthemeUid: (input?.giftMediaTab && input?.giftTheme?.uid) || '',
      giftmediaUid: (input?.giftMediaTab && input?.giftMedia?.uid) || undefined,
      giphyId: input?.giftMediaTab && input?.giphy?.id,
      giphyUrl: input?.giftMediaTab && input?.giphy?.url,
      guestFirstName: input?.guestFirstName || '',
      guestLastName: input?.guestLastName || '',
      guestEmail: input?.guestEmail || '',
      type: 'friend',
      recipients: encodeRecipients(input?.recipients || []),
      scheduledDeliveryAt,
      recipientTimezone,
      message: (input?.personaliseMessageTab && input?.message) || '',
    };

    return snakeKeys(guestFriendPayload) as GuestCheckoutFriendPayload;
  }
};

export const formatValue = (unformattedValue: string): string | null => {
  const valueIncludesCentsRegex = /^\d+.\d{2}$/;
  const valueExcludesCentsRegex = /^\d+$/;
  const includesCents = valueIncludesCentsRegex.test(unformattedValue);
  const excludesCents = valueExcludesCentsRegex.test(unformattedValue);

  if (includesCents) {
    return unformattedValue;
  } else if (excludesCents) {
    return unformattedValue + '.00';
  } else {
    return null;
  }
};

export const trimToLength = (string: string, length: number) => {
  if (string.length <= length) {
    return string;
  } else {
    return string.substring(0, length);
  }
};

export const getRecipientDetails = (fields: ProductFormInputs, user: User) => {
  if (fields.purchaseType === 'self') {
    if (user && user?.firstName && user?.lastName) {
      return `${user.firstName} ${user.lastName}`;
    } else if (fields?.guestFirstName && fields?.guestLastName) {
      return `${fields.guestFirstName} ${fields.guestLastName}`;
    } else {
      return 'Myself';
    }
  } else if (fields.purchaseType === 'friend') {
    const numberOfRecipients = fields?.recipients?.length;

    const subtitle = numberOfRecipients > 1 ? `${numberOfRecipients} recipients` : fields?.recipients[0]?.name || '';

    return subtitle;
  }

  return undefined;
};

export const getWrapSubtitle = (fields: ProductFormInputs) => {
  const wrapTabKeys = ['giftMediaTab', 'giftCardDesignTab', 'personaliseMessageTab'];
  let tabsSelected = 0;

  wrapTabKeys.forEach(key => {
    fields?.[key] && tabsSelected++;
  });

  switch (tabsSelected) {
    case 0:
      return '';
    case 1:
      return 'One selected';
    case 2:
      return 'Two selected';
    case 3:
      return 'All selected';
    default:
      return '';
  }
};
